import type React from "react";
import { useFormContext } from "react-hook-form";
import type { TournamentApplyInput } from "../../../types/tournament.types";
import type { CalendarItem } from "../../../@apis/dto/response/torunament.response";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import { formatDate } from "../../../lib/utils/dateUtils";
import { parseBirth, getAgeType } from "../../../lib/utils/birthdayUtils";
import { REGION_OPTIONS } from "../../../lib/constants/region.constants";

const regionLabel = (value?: string) =>
  REGION_OPTIONS.find((r) => r.value === value)?.label ?? value ?? "-";

interface Props {
  tournamentName: string;
  calendarList: CalendarItem[];
  hasClass: boolean;
  /** 참가비 (원) */
  entryFee: number;
  isCertificateAccept?: boolean;
}

// 라벨/값 한 줄
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-[8px] items-start">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      <p className="flex-1 min-w-0 text-black break-keep whitespace-pre-line [overflow-wrap:anywhere]">
        {value}
      </p>
    </div>
  );
}

// 흰 카드
function InfoCard({
  title,
  children,
  gap = "gap-[12px]",
}: {
  title: string;
  children: React.ReactNode;
  gap?: string;
}) {
  return (
    <div
      className={`w-full bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px] flex flex-col ${gap}`}
    >
      <p className="text-base font-semibold text-black">{title}</p>
      {children}
    </div>
  );
}

export default function CompetitionPaymentConfirm({
  tournamentName,
  calendarList,
  hasClass,
  entryFee,
  isCertificateAccept,
}: Props) {
  const { myInfo } = useMyInfoStore();
  const { watch } = useFormContext<TournamentApplyInput>();

  const calendarId = watch("calendarId");
  const email = watch("email");
  const region = watch("region");
  const haveProCertificate = watch("haveProCertificate");

  const calenderItem = calendarList.find((el) => el.id === calendarId);
  const totalAmount = entryFee;

  return (
    <section className="flex flex-col gap-[12px]">
      <InfoCard title="신청 대회 정보">
        <div className="flex flex-col gap-[8px] text-sm font-medium">
          <InfoRow label="대회명" value={tournamentName} />
          <InfoRow
            label="대회날짜"
            value={formatDate(calenderItem?.date || "", "YYYY-MM-DD (ddd)")}
          />
          <InfoRow label="대회일정" value={calenderItem?.timeType || "-"} />
        </div>
      </InfoCard>

      <InfoCard title="내 신청 정보">
        <div className="flex flex-col gap-[8px] text-sm font-medium">
          <InfoRow label="이름" value={myInfo.name} />
          <InfoRow
            label="생년월일"
            value={parseBirth(myInfo.birthdate).format("YYYY년 M월 D일")}
          />
          <InfoRow
            label="성별"
            value={myInfo.gender == "1" ? "남성" : "여성"}
          />
          <InfoRow label="이메일" value={email || "-"} />
          {region && <InfoRow label="지역" value={regionLabel(region)} />}
          {isCertificateAccept !== false && (
            <InfoRow
              label="프로자격증"
              value={haveProCertificate ? "보유" : "미보유"}
            />
          )}
          {hasClass && (
            <InfoRow label="클래스" value={getAgeType(myInfo.birthdate)} />
          )}
        </div>
      </InfoCard>

      <InfoCard title="결제 정보" gap="gap-[20px]">
        <div className="flex flex-col gap-[8px] text-sm font-medium">
          <InfoRow label="참가비" value={`${entryFee.toLocaleString()}원`} />
        </div>

        <div className="bg-[#f2f2f7] rounded-[10px] p-[8px] flex items-center justify-between">
          <p className="text-base font-semibold text-black">결제 예정 금액</p>
          <p className="text-xl font-medium text-[#007aff]">
            {totalAmount.toLocaleString()}원
          </p>
        </div>
      </InfoCard>
    </section>
  );
}
